import type { RuleGroupType, RuleType } from "react-querybuilder";
import { normalizePlanTypeApiCode } from "../../../services/autoTaggingService";
import {
  createEmptyConditionValue,
  parseEnhancedTaggingConditionValue,
  type EnhancedTaggingConditionValue,
} from "./enhancedTaggingConditionValue";
import type { EnhancedTaggingThenTag } from "./EnhancedTaggingRuleThenStep";

/** Leaf rule shape expected by POST /auto-tagging/conditional-plans. */
export type ConditionalPlanRuleValueItem = {
  code: string;
  name: string;
};

export type ConditionalPlanRulePayload = {
  field: string;
  operator: string;
  value: Array<string | ConditionalPlanRuleValueItem>;
  valueSource: "value";
};

/** Nested condition group for conditional plans. */
export type ConditionalPlanConditionPayload = {
  combinator: string;
  rules: Array<ConditionalPlanRulePayload | ConditionalPlanConditionPayload>;
  not: boolean;
};

export type ConditionalPlanCreatePayload = {
  target_type: string;
  sub_type: string;
  condition: ConditionalPlanConditionPayload;
  concept: string;
  concept_value: {
    name: string;
    code: string;
    description: string;
  };
};

function isRuleGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

function mapApiValueToConditionValue(value: unknown): EnhancedTaggingConditionValue {
  if (Array.isArray(value)) {
    const conceptValues = value
      .map((item) => {
        if (item && typeof item === "object") {
          const record = item as Record<string, unknown>;
          const code = String(record.code ?? record.value ?? "").trim();
          if (!code) return null;
          const name =
            String(record.name ?? record.label ?? record.display_name ?? "").trim() ||
            code;
          return { id: code, code, name };
        }
        const code = String(item ?? "").trim();
        if (!code) return null;
        return { id: code, code, name: code };
      })
      .filter((item): item is { id: string; code: string; name: string } => item !== null);

    if (conceptValues.length === 1 && conceptValues[0].code === "*") {
      return { allValues: true, conceptValues: [] };
    }
    return {
      allValues: false,
      conceptValues,
    };
  }

  return parseEnhancedTaggingConditionValue(value);
}

function mapConditionRule(rule: RuleType): ConditionalPlanRulePayload {
  const parsed = parseEnhancedTaggingConditionValue(rule.value);
  const value = parsed.allValues
    ? ["*"]
    : parsed.conceptValues
        .map((item) => {
          const code = item.code.trim();
          if (!code) return null;
          return {
            code,
            // Persist display name so list Condition Description can show names.
            name: item.name.trim() || code,
          };
        })
        .filter((item): item is ConditionalPlanRuleValueItem => item !== null);

  return {
    field: String(rule.field ?? "").trim(),
    operator: String(rule.operator ?? "").trim(),
    value,
    valueSource: "value",
  };
}

export function mapConditionQueryToPayload(
  query: RuleGroupType,
): ConditionalPlanConditionPayload {
  return {
    combinator: String(query.combinator ?? "and"),
    not: Boolean(query.not),
    rules: query.rules.map((item) => {
      if (isRuleGroup(item)) {
        return mapConditionQueryToPayload(item);
      }
      return mapConditionRule(item as RuleType);
    }),
  };
}

/** Hydrate the condition builder from an API condition payload. */
export function mapConditionPayloadToEditorQuery(
  condition: unknown,
): RuleGroupType {
  if (!isRuleGroup(condition) || !Array.isArray(condition.rules)) {
    return {
      combinator: "or",
      rules: [
        {
          combinator: "and",
          rules: [
            {
              field: "",
              operator: "containsAny",
              value: createEmptyConditionValue(),
            },
          ],
        },
      ],
    };
  }

  const mapGroup = (group: RuleGroupType): RuleGroupType => ({
    combinator: String(group.combinator ?? "and"),
    not: Boolean(group.not),
    rules: (group.rules ?? []).map((item) => {
      if (isRuleGroup(item)) {
        return mapGroup(item);
      }
      const rule = item as RuleType;
      return {
        field: String(rule.field ?? "").trim(),
        operator: String(rule.operator ?? "containsAny").trim() || "containsAny",
        value: mapApiValueToConditionValue(rule.value),
        valueSource: "value" as const,
      };
    }),
  });

  return mapGroup(condition);
}

export function buildConditionalPlanCreatePayload(
  conditionQuery: RuleGroupType,
  thenTag: EnhancedTaggingThenTag,
  objectSelection?: { objectType: string; objectSubType: string },
): ConditionalPlanCreatePayload {
  return {
    target_type: normalizePlanTypeApiCode(objectSelection?.objectType ?? ""),
    sub_type: normalizePlanTypeApiCode(objectSelection?.objectSubType ?? ""),
    condition: mapConditionQueryToPayload(conditionQuery),
    concept: thenTag.dimensionCode.trim(),
    concept_value: {
      name: thenTag.valueLabel.trim() || thenTag.valueCode.trim(),
      code: thenTag.valueCode.trim(),
      description: "",
    },
  };
}
